"use client";

import dynamic from "next/dynamic";
import { useState, type FocusEvent, type PointerEvent } from "react";

import BirthToolFormShell from "@/components/tool-shell/BirthToolFormShell";
import {
  YOGA_CHECK_FIELD_IDS,
  YOGA_CHECK_FRAME,
  yogaCheckFormProps,
} from "@/components/yoga/yoga-check-form";

import { YOGA_HUB_CHECK_RESERVE, type YogaHubRow } from "./yoga-hub-check-copy";

// The /yoga/ hub check's one boundary, the guides' pattern (YogaChartCheckLazy):
// the hub's section imports only this module, and it owns the one dynamic() to
// the island. The form, place search, kundali client, verdicts and analytics
// load as an async chunk; the first load gains only this file and the shell.
//
// Activation is element-scoped and local: no window listener and no timer, so
// a reader scrolling the hub never downloads the island and the deploy's body
// pointerdown smoke loads nothing; a press, focus or submit on the shell does.
//
// The birth time is optional, as on the Gajakesari and Budhaditya guides: the
// date settles those two on many days, and the rows that read the Lagna
// (Malavya, Viparita, Neecha Bhanga) say they need the time.
//
// The slot reserves the tallest state, which every state fills, so neither
// shell -> form nor a slow engine reply -> answer (or error) moves the page
// below. Natural heights measured 2026-10-15 (next dev + Playwright on /yoga/,
// POST /kundali mocked with the engine's verbatim replies, 7 widths from 320
// to 1280): the shell is 436 at 320-390 px wide and 420 above; the form 436,
// 420 from 414 and 362 from 640. The tallest state is the no-time answer (five
// rows, the Lagna note and "Add your birth time"): 582 at 320-360, 561 at 390,
// 536 at 414-639 and 552 from 640; then the engine's place error (560 at
// 320-390, 452 from 640) and the form with "Don't know your birth time?" open
// (538 at 320, 496 from 640). Hence the guides' 700 px on phones and 580 px
// from the sm breakpoint. With them the slot held its height and the
// section's bottom never moved in all 49 runs. The guides' form frame pins the
// submit to the slot's foot, and its loading label is 4 px shorter, so the
// submit drops 4 px on the press and rises back after an error (layout shift
// 0.0001-0.0002, on the error runs only): the guides' own form, unchanged.
// translate="no" covers the shell, form and answer, which re-render on "Check
// another birth date" (tests/translate-safe-islands.test.ts).

const SHELL = { ...yogaCheckFormProps(false), className: YOGA_CHECK_FRAME };

const YogaHubCheck = dynamic(() => import("./YogaHubCheck"), {
  ssr: false,
  loading: () => <BirthToolFormShell {...SHELL} />,
});

/** BirthToolFormShell input names -> the real form's input ids. */
const SHELL_FIELD_IDS: Readonly<Record<string, string>> = {
  dob: YOGA_CHECK_FIELD_IDS.date,
  tob: YOGA_CHECK_FIELD_IDS.time,
  place: YOGA_CHECK_FIELD_IDS.place,
};

function formFieldId(target: EventTarget | null): string {
  const name = target instanceof Element ? target.getAttribute("name") : null;
  return (name && SHELL_FIELD_IDS[name]) || YOGA_CHECK_FIELD_IDS.date;
}

export default function YogaHubCheckLazy({ rows }: { rows: readonly YogaHubRow[] }) {
  const [active, setActive] = useState(false);
  const [focusFieldId, setFocusFieldId] = useState<string | null>(null);

  // The shell's own onActivate cannot tell a keyboard focus from a touch, so
  // the slot reads the intent in the capture phase: focus follows the visitor
  // into the real form after a keyboard focus, a mouse press or a submit, and
  // never after a touch, which may be the start of a scroll.
  const shellIntent = active
    ? {}
    : {
        onPointerDownCapture: (event: PointerEvent<HTMLDivElement>) =>
          setFocusFieldId(event.pointerType === "mouse" ? formFieldId(event.target) : null),
        onFocusCapture: (event: FocusEvent<HTMLDivElement>) =>
          setFocusFieldId(formFieldId(event.target)),
        onSubmitCapture: () => setFocusFieldId(YOGA_CHECK_FIELD_IDS.date),
      };

  return (
    <div
      translate="no"
      data-yoga-hub-check-slot=""
      className={`flex flex-col ${YOGA_HUB_CHECK_RESERVE}`}
      {...shellIntent}
    >
      {active ? (
        <YogaHubCheck rows={rows} focusFieldId={focusFieldId} />
      ) : (
        <BirthToolFormShell {...SHELL} onActivate={() => setActive(true)} />
      )}
    </div>
  );
}
